/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { AxiosRequestConfig, AxiosResponse } from 'axios';
import apiClient from '../apiClient';
import {
    cutEmptyParams,
    cutEmptyParamsIncludingArrays,
    DATA_UNAVAILABLE,
    makeArrayFromCommaSeparatedString,
} from '../../../utils/utils';
import {
    DocumentsListDto,
    DocumentsListRequestParams,
    DocumentsListExcelRequestParams,
    DocumentResource,
    ProductItemAiCategories,
} from './documentsClient.typings';
import {
    checkIsDocNumbersFilterShown,
    checkIsTimeRangeFilterHidden,
    DocumentsListTableData,
} from '../../../components/RegistryWidgets/Documents/DocumentsList/DocumentsListConfig';
import { RequestToCreateExcelDto } from '../downloadings/downloadingsClient.typings';

const SEARCH_TYPE_TO_SEARCH_PARAM: { [key in keyof DocumentsListTableData]?: string } = {
    documentId: 'documentIds',
    parentDocumentId: 'parentDocumentIds',
    cashBoxTaxNumber: 'cashBoxTaxNumbers',
    storeTaxNumber: 'storeTaxNumbers',
    companyTaxNumber: 'companyTaxNumbers',
    docNumber: 'docNumbers',
    globalDocumentNumber: 'globalDocNumbers',
    documentNumberInShift: 'positionsInShift',
    rrn: 'rrns',
};

const getSearchParamName = (searchType: keyof DocumentsListTableData): string => {
    let searchParam = SEARCH_TYPE_TO_SEARCH_PARAM[searchType];
    if (searchParam === undefined) searchParam = DATA_UNAVAILABLE;
    return searchParam;
};

const adjustDocumentsListFilterParams = (params: DocumentsListRequestParams | DocumentsListExcelRequestParams) => {
    if (checkIsTimeRangeFilterHidden(params)) {
        delete params.dateFrom;
        delete params.dateTo;
    }

    let queryParams = {
        ...params,
        [getSearchParamName(params.search_type as keyof DocumentsListTableData)]: makeArrayFromCommaSeparatedString(
            params.search_text,
            true,
        ),
    };
    if (checkIsDocNumbersFilterShown(params)) {
        queryParams = {
            ...queryParams,
            [getSearchParamName(params.docNumbersSearchType as keyof DocumentsListTableData)]:
                makeArrayFromCommaSeparatedString(params.docNumbersSearchText, true),
        };
    }
    delete queryParams.search_text;
    delete queryParams.search_type;
    delete queryParams.docNumbersSearchType;
    delete queryParams.docNumbersSearchText;

    return cutEmptyParamsIncludingArrays(queryParams);
};

async function listDocuments(params: DocumentsListRequestParams): Promise<DocumentsListDto> {
    return apiClient.post<DocumentsListDto>('1.0.0/documents', adjustDocumentsListFilterParams(params));
}

async function downloadDocumentsAsExcel(params: DocumentsListExcelRequestParams): Promise<RequestToCreateExcelDto> {
    return apiClient.post<RequestToCreateExcelDto>('1.0.0/documents/excel', adjustDocumentsListFilterParams(params));
}

function getProductItemsAiCategories(documentId: string): Promise<ProductItemAiCategories[]> {
    return apiClient.get<ProductItemAiCategories[]>(`1.0.0/documents/${documentId}/aiCategories`);
}

function getDocumentCard(documentId: string, taxNumber?: string): Promise<DocumentResource> {
    return apiClient.get<DocumentResource>(
        `1.0.0/documents/${documentId}/card`,
        taxNumber ? { tax_number: taxNumber } : null,
    );
}

async function getDocumentCardPict(
    cashBoxTaxNumber: string,
    createdAtUtc: string,
    documentIdHash: string,
): Promise<ArrayBuffer> {
    const config: AxiosRequestConfig = {
        params: cutEmptyParams({ cashBoxTaxNumber, createdAtUtc, documentIdHash }),
        responseType: 'arraybuffer',
    };

    return apiClient.getWithConfig<ArrayBuffer>('1.0.0/documents/cardPict', config);
}

async function getDocumentPict(
    token: string,
    documentId: string,
    taxNumber?: string,
): Promise<AxiosResponse<ArrayBuffer>> {
    const config: AxiosRequestConfig = {
        params: taxNumber ? { tax_number: taxNumber } : null,
        responseType: 'arraybuffer',
    };
    if (token) config.headers = { Token: token };

    return apiClient.getWithFullResponse<ArrayBuffer>(`2.0.0/documents/${documentId}`, config);
}

export const documentsClient = {
    listDocuments,
    downloadDocumentsAsExcel,
    getProductItemsAiCategories,
    getDocumentCard,
    getDocumentCardPict,
    getDocumentPict,
};
